import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Book, Cross, Users, Zap, Heart, Church, User, Globe, ArrowRight } from 'lucide-react';

const StatementOfFaithPage = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const coreBeliefs = [
    {
      title: 'The Scriptures',
      icon: Book,
      summary: 'We believe that the Holy Bible, consisting of the 66 books of the Old and New Testaments, is the inspired, infallible, and authoritative Word of God.',
      verse: '"All Scripture is breathed out by God and profitable for teaching, for reproof, for correction, and for training in righteousness." — 2 Timothy 3:16 (ESV)',
      details: 'The Holy Bible is the supreme source of truth for Christian beliefs and living. It is the inspired, infallible, and authoritative Word of God, consisting of the 66 books of the Old and New Testaments.'
    },
    {
      title: 'The Trinity',
      icon: Cross,
      summary: 'We believe in one God, eternally existing in three persons: the Father, the Son, and the Holy Spirit.',
      verse: '"Go therefore and make disciples of all nations, baptizing them in the name of the Father and of the Son and of the Holy Spirit." — Matthew 28:19 (ESV)',
      details: 'These three are co-equal and co-eternal, each possessing all the attributes of deity. We believe in the unity of the one true God who exists eternally in three distinct persons.'
    },
    {
      title: 'God the Father',
      icon: Users,
      summary: 'We believe that God the Father is the Creator and Sustainer of all things. He is sovereign, holy, loving, and just.',
      verse: '"Yet for us there is one God, the Father, from whom are all things and for whom we exist." — 1 Corinthians 8:6a (ESV)',
      details: 'He is the Father of our Lord Jesus Christ and of all who believe in Him. God the Father is sovereign over all creation and demonstrates perfect love and justice.'
    },
    {
      title: 'Jesus Christ',
      icon: Heart,
      summary: 'We believe that Jesus Christ is the eternal Son of God, fully God and fully man.',
      verse: '"For there is one God, and there is one mediator between God and men, the man Christ Jesus." — 1 Timothy 2:5 (ESV)',
      details: 'He was conceived by the Holy Spirit, born of the Virgin Mary, lived a sinless life, died on the cross as a substitutionary sacrifice for our sins, rose bodily from the dead, ascended into heaven, and will return in glory to judge the living and the dead.'
    },
    {
      title: 'The Holy Spirit',
      icon: Zap,
      summary: 'We believe that the Holy Spirit is the third person of the Trinity, who convicts the world of sin, regenerates believers.',
      verse: '"But the Helper, the Holy Spirit, whom the Father will send in my name, he will teach you all things and bring to your remembrance all that I have said to you." — John 14:26 (ESV)',
      details: 'The Holy Spirit indwells, guides, equips, and empowers believers for godly living and service. He is the agent of regeneration and sanctification in the believer\'s life.'
    },
    {
      title: 'The Nature of Humanity and Sin',
      icon: User,
      summary: 'We believe that humanity was created in the image of God, but through Adam\'s disobedience, sin entered the world.',
      verse: '"Therefore, just as sin came into the world through one man, and death through sin, and so death spread to all men because all sinned." — Romans 5:12 (ESV)',
      details: 'This original sin resulted in both physical and spiritual death, corrupting human nature and rendering all people inherently sinful and separated from God.'
    },
    {
      title: 'Salvation',
      icon: Cross,
      summary: 'We believe that salvation is a gift of God\'s grace, received through faith in Jesus Christ alone.',
      verse: '"For by grace you have been saved through faith. And this is not your own doing; it is the gift of God." — Ephesians 2:8 (ESV)',
      details: 'Salvation is not earned by human effort but is granted by God\'s mercy to those who repent and believe. Through Christ\'s atoning death and resurrection, believers are justified, adopted into God\'s family, and granted eternal life.'
    },
    {
      title: 'The Church',
      icon: Church,
      summary: 'We believe that the Church is the body of Christ, composed of all true believers.',
      verse: '"Now you are the body of Christ and individually members of it." — 1 Corinthians 12:27 (ESV)',
      details: 'The Church exists to glorify God, edify believers, and evangelize the world. The local church is a community of believers who gather for worship, teaching, fellowship, and service.'
    },
    {
      title: 'Ordinances',
      icon: Globe,
      summary: 'We believe that the Lord Jesus Christ instituted two ordinances for the Church: baptism and the Lord\'s Supper.',
      verse: '"Do this in remembrance of me." — Luke 22:19b (ESV)',
      details: 'Baptism is an outward testimony of an inward transformation, symbolizing the believer\'s identification with Christ\'s death, burial, and resurrection. The Lord\'s Supper is a commemoration of Christ\'s death and celebration of communion with Him and other believers.'
    },
    {
      title: 'The Return of Christ',
      icon: ArrowRight,
      summary: 'We believe in the personal, visible, and imminent return of Jesus Christ.',
      verse: '"Behold, I am coming soon, bringing my recompense with me, to repay each one for what he has done." — Revelation 22:12 (ESV)',
      details: 'His return will bring about the final judgment, the eternal state of believers in heaven, and unbelievers in hell.'
    },
    {
      title: 'Christian Living',
      icon: Heart,
      summary: 'We believe that every believer is called to live a life of holiness, empowered by the Holy Spirit.',
      verse: '"I appeal to you therefore, brothers, by the mercies of God, to present your bodies as a living sacrifice, holy and acceptable to God, which is your spiritual worship." — Romans 12:1 (ESV)',
      details: 'This includes regular prayer, study of the Scriptures, fellowship with other believers, and active participation in the mission of the Church.'
    }
  ];

  const toggleSection = (index) => {
    setExpandedSection(expandedSection === index ? null : index);
  };

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0">
      {/* Hero Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 lg:mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 font-display">
              Statement of Faith
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto mb-8 font-sans">
              El Bethel Ministries is built on a foundation of orthodox Christian beliefs that guide our ministry, 
              teaching, and community life. These biblical truths shape every aspect of our worship and service.
            </p>
            <div className="bg-[#f17b21] text-white px-6 py-4 rounded-lg inline-block max-w-4xl">
              <p className="font-semibold font-sans text-sm lg:text-base">
                "All Scripture is breathed out by God and profitable for teaching, for reproof, for correction, and for training in righteousness" - 2 Timothy 3:16
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Beliefs */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 font-display">Our Foundational Beliefs</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto font-sans">
              These unchanging biblical truths form the bedrock of our ministry and guide every aspect of our teaching and community life.
            </p>
          </div>

          <div className="space-y-4 max-w-5xl mx-auto">
            {coreBeliefs.map((belief, index) => {
              const IconComponent = belief.icon;
              const isExpanded = expandedSection === index;
              
              return (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <button
                    onClick={() => toggleSection(index)}
                    className="w-full p-4 lg:p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 lg:w-12 lg:h-12 bg-[#f17b21] rounded-full flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                        </div>
                        <div className="text-left flex-1">
                          <h3 className="text-lg lg:text-xl font-bold text-gray-800 font-display">{belief.title}</h3>
                          <p className="text-gray-600 mt-1 font-sans text-sm lg:text-base">{belief.summary}</p>
                        </div>
                      </div>
                      <div className="flex-shrink-0 ml-4">
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 lg:w-6 lg:h-6 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 lg:w-6 lg:h-6 text-gray-500" />
                        )}
                      </div>
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div className="px-4 lg:px-6 pb-4 lg:pb-6 border-t border-gray-100">
                      <div className="pt-6">
                        <p className="text-gray-700 leading-relaxed mb-4 font-sans">{belief.details}</p>
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <p className="text-blue-800 italic font-medium font-sans text-sm lg:text-base">{belief.verse}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ministry Foundation */}
      <section className="py-12 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8 font-display">
              Built on Biblical Foundation
            </h2>
            <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg">
              <p className="text-lg lg:text-xl text-gray-700 leading-relaxed mb-6 font-sans">
                Every aspect of our ministry - from biblical teaching to community outreach - 
                is guided by these unchanging biblical truths. We are committed to faithful 
                exposition of Scripture and authentic Christian community.
              </p>
              <div className="bg-gradient-to-r from-[#f17b21] to-[#ff9649] text-white px-6 py-4 rounded-lg">
                <p className="font-semibold font-sans">
                  "Heaven and earth will pass away, but my words will not pass away." - Matthew 24:35
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StatementOfFaithPage;